



















































































































































































































import Vue from 'vue';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import {
    computed,
    defineComponent,
    watch,
} from '@vue/composition-api';
import { capitalize } from '@superbet-group/web.lib.utilities';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    SdsButton,
    SdsIcon,
    SdsIllustration,
} from '@superbet-group/web.lib.ui-components';
import { isBelgium } from '@modules/layout/utils/isCountry';
import { RouteName } from '@src/app/router/constants/RouteName';
import { formattedToTwoDecimals } from '@src/app/utils/balance';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import modalStore from '@store/modules/ui/shared/modal/modalIndex';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import {
    ClickEventType,
    createClickAnalyticsPayload,
} from '@src/app/utils/clickAnalytics';
import { RafClickValues } from '@core/models/referAFriend/enums';
import useReferAFriendFlag from '@shared/composables/useReferAFriendFlag';
import { useLoyaltyFlag } from '@shared/composables/useLoyaltyFlag';
import { useAccountMenuSections } from '@shared/composables/useAccountMenuSections';
import { isResponsibleGamingElementEnabled } from '@accountFederation/modules/responsibleGaming/helpers';
import { ResponsibleGamingPageElement } from '@src/config/shell/localConfigTypes';
import { ClickName } from '@superbet-group/product.analytics';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import AccountRgInfoStats from '@app/shared/components/Account/AccountRgInfoStats.vue';
import MenuItem from '@app/shared/components/Account/MenuItem.vue';
import loyaltyIndex from '@store/modules/data/loyalty/loyaltyIndex';

export default defineComponent({
    name: 'AccountMenuBody',
    emits: ['hideDropdown'],
    props: {
        isPage: {
            type: Boolean,
            default: false,
        },
    },
    setup(_, { emit }) {
        const { pushRoute: goTo, route } = useVueRouter();

        const pushRoute = ({ name }: { name: string }) => {
            if (route.value.name === name) {
                emit('hideDropdown');
            } else {
                goTo({ name });
            }
        };

        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const userBalance = authModule.getter('userBalance');
        const user = authModule.getter('user');
        const isLoggedIn = computed(() => !!user.value);
        const logoutUser = authModule.action('logout');

        const modalModule = useVuexStore<typeof modalStore>('ui/modal');
        const setModal = modalModule.action('setModal');
        const clearModals = modalModule.action('clearModals');

        const {
            balance: supercoins,
            refreshBalance: refreshSupercoinsBalance,
        } = useVuexStore<typeof loyaltyIndex>('data/loyalty').unpack();

        const { isReferAFriendEnabled } = useReferAFriendFlag();
        const { isSupercoinsEnabled } = useLoyaltyFlag();

        const login = () => {
            if (!isLoggedIn.value) {
                emit('hideDropdown');
                setModal({ code: modalCodes.login });
            }
        };
        const logout = () => {
            emit('hideDropdown');
            logoutUser();
            clearModals();
        };

        watch([isSupercoinsEnabled], () => {
            if (isSupercoinsEnabled.value) {
                refreshSupercoinsBalance();
            }
        });

        const { profileSectionItems, bonusSectionItems } = useAccountMenuSections(pushRoute, emit);
        const responsibleGamingBE = computed(() => {
            return [
                (isResponsibleGamingElementEnabled(ResponsibleGamingPageElement.DepositLimit) ? {
                    tag: 'deposit-limit',
                    icon: 'commerce-deposit-alt',
                    label: 'deposit limit',
                    action: () => pushRoute({ name: RouteName.Account.DepositLimit }),
                } : undefined),
                {
                    tag: 'self-exclusion',
                    icon: 'status-disabled',
                    label: 'pam.accountMenu.blockMyProfile',
                    action: () => pushRoute({ name: RouteName.Account.SelfExclusion }),
                },
            ].filter((element) => element !== undefined);
        });
        const hasResponsibleGaming = computed(() =>
            shellLocalConfig.getAccountSidebarMenuItems().some((item) => item.id === RouteName.Account.ResponsibleGaming),
        );
        const baseResponsibleGaming = [
            {
                type: 'link',
                tag: 'responsible-gaming',
                icon: 'games-responsible-gambling',
                label: 'responsible gaming',
                subtextTitle: 'pam.accountMenu.responsibleGamingSubtext',
                action: () => pushRoute({ name: RouteName.Account.ResponsibleGaming }),
            },
        ];

        const settings = computed(() => ({
            profile: profileSectionItems.value,
            bonuses: bonusSectionItems.value,
            responsibleGaming: [
                ...(hasResponsibleGaming.value ? baseResponsibleGaming : []),
                ...(isBelgium() ? responsibleGamingBE.value : []),
            ],
            settings: [{
                type: 'link',
                tag: 'settings',
                icon: 'navigation-settings',
                label: 'settings',
                action: () => pushRoute({ name: RouteName.Account.Settings }),
            }],
            help: [{
                type: 'link',
                tag: 'help-and-support',
                icon: 'status-help',
                label: 'helpAndSupport',
                action: () => pushRoute({ name: RouteName.Account.HelpAndSupport }),
            }],
            logout: [{
                type: 'item',
                tag: 'logout',
                icon: 'actions-logout',
                label: 'logout',
                action: logout,
            }],
            // ...here go other sections with optios
        }));

        function onReferAFriendClick() {
            // @TODO(corneliu-iancu): Temporary dual analytics implementation.
            // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
            // Both libraries are being used in parallel to validate data consistency between them.
            // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
            // Once validation is complete, $superAnalytics will be deprecated and removed.
            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.LOYALTY_RAF_ENTRYPOINT_CLICK,
                click: {
                    click_value: RafClickValues.UserTopMenu,
                    type: 'general_click',
                },
            });
            Vue.prototype.$superAnalytics.sendEvent(
                createClickAnalyticsPayload(ClickEventType.loyaltyRafEntrypointClick, RafClickValues.UserTopMenu),
            );

            pushRoute({ name: RouteName.Account.ReferAFriend });
        }

        function onLoyaltySupercoinsClick() {
            AnalyticsSDK.getInstance().log(
                'Loyalty_Shop_Opened',
                {
                    source: 'Account_Menu',
                },
            );
            pushRoute({ name: RouteName.Loyalty.Supercoins });
        }

        const goToDepositRoute = () => {
            emit('hideDropdown');
            AnalyticsSDK.getInstance().log(
                'Deposit_Option_Chosen',
                {
                    whichOption: 'User_menu',
                },
            );
            pushRoute({ name: RouteName.Account.Deposit });
        };

        const balance = computed(() => {
            if (userBalance.value == null || userBalance.value === '-' || isNaN(Number(userBalance.value))) {
                return undefined;
            }
            return formattedToTwoDecimals(userBalance.value);
        });

        return {
            user,
            login,
            capitalize,
            hasAccountMenuRgInfo: getAccountConfig().hasAccountMenuRgInfo,
            balance,
            supercoins,
            register: () => pushRoute({ name: RouteName.Account.Register }),
            settings,
            isLoggedIn,
            isReferAFriendEnabled,
            onReferAFriendClick,
            onLoyaltySupercoinsClick,
            isSupercoinsEnabled,
            goToDepositRoute,
            goToWithdrawRoute: () => pushRoute({ name: RouteName.Account.Withdrawal }),
            showTransactions: () => pushRoute({ name: RouteName.Account.History }),
            RouteName,
            hasCoinShopAccountMenuLink: shellLocalConfig.hasCoinShopAccountMenuLink,
            depositRoute: { name: RouteName.Account.Deposit },
            withdrawalRoute: { name: RouteName.Account.Withdrawal },
        };
    },
    components: {
        MenuItem,
        SdsButton,
        SdsIcon,
        SdsIllustration,
        AccountRgInfoStats,
    },
});

